import zod from 'zod';
import { WebhookIntegrationFormSchema } from '../../../services/IntegrationService/WebhookService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateWebhookIntegrationAsync,
  useGetAllWebhookPayloadsByLicenseAsync,
  useGetWebhookIntegrationByIdAsync,
  useUpdateWebhookIntegrationAsync,
} from '../../../services/IntegrationService/WebhookService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { IWebhookPayloadSimpleProps } from '../../../services/IntegrationService/WebhookService/types';
import DropdownComponent from '../../../components/DropdownComponent';
import { Edit } from '@mui/icons-material';
import WebhookPayloadEditorModal from './WebhookPayloadEditorModal';
import TooltipComponent from '../../../components/TooltipComponent';
import { t } from 'i18next';

interface WebhookEditionFormProps {
  webhookIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type WebhookEditionFormInput = zod.infer<typeof WebhookIntegrationFormSchema>;

const WebhookEditionForm = ({
  webhookIdToUpdate,
  handleSaveForm,
}: WebhookEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const [payloads, setPayloads] = useState<IWebhookPayloadSimpleProps[]>([]);
  const { control, handleSubmit, reset } = useForm<WebhookEditionFormInput>({
    resolver: zodResolver(WebhookIntegrationFormSchema),
    defaultValues: {
      name: '',
      url: '',
      token: '',
      password: '',
      username: '',
      description: '',
      isActive: true,
    },
  });
  const [isOpenPayloadEditorModal, setIsOpenPayloadEditorModal] =
    useState<boolean>(false);

  const { mutate: mutateCreateWebhookIntegration } =
    useCreateWebhookIntegrationAsync();
  const { mutate: mutateUpdateWebhookIntegration } =
    useUpdateWebhookIntegrationAsync();
  const { mutateAsync: mutateGetWebhookIntegrationByIdAsync } =
    useGetWebhookIntegrationByIdAsync();
  const { mutateAsync: mutateGetAllWebhookPayloadsAsync } =
    useGetAllWebhookPayloadsByLicenseAsync();

  const handleSave = (data: WebhookEditionFormInput) => {
    if (webhookIdToUpdate) {
      mutateUpdateWebhookIntegration(
        { id: webhookIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração webhook atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateWebhookIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração webhook criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  const handleOpenPayloadEditor = () => {
    setIsOpenPayloadEditorModal(true);
  };

  const getPayloadTooltipDescription = (value?: number) => {
    let description = '';
    if (value) {
      const payload = payloads.find((v) => v.id == value);
      if (payload) {
        description = payload.payload;
      }
    }
    return description;
  };

  useEffect(() => {
    if (webhookIdToUpdate) {
      mutateGetWebhookIntegrationByIdAsync(webhookIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [webhookIdToUpdate]);

  useEffect(() => {
    mutateGetAllWebhookPayloadsAsync().then(setPayloads);
  }, []);
  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.webHookForms.name')}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.webHookForms.url')}
              />
            )}
            name={'url'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.webHookForms.token')}
              />
            )}
            name={'token'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.webHookForms.user')}
              />
            )}
            name={'username'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.webHookForms.password')}
              />
            )}
            name={'password'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TooltipComponent title={getPayloadTooltipDescription(value)}>
                <DropdownComponent
                  value={value}
                  options={payloads}
                  optionLabel={'name'}
                  optionValue={'id'}
                  onChange={(val) => onChange(Number(val))}
                  label={t('integrationsPage.webHookForms.bodyRequisition')}
                  additionalButton={
                    <ButtonComponent
                      sx={{ height: 55 }}
                      onClick={() => handleOpenPayloadEditor()}
                    >
                      <Edit />
                    </ButtonComponent>
                  }
                />
              </TooltipComponent>
            )}
            name={'payloadId'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.webHookForms.description')}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.webHookForms.active')}
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent fullWidth onClick={handleSubmit(handleSave)}>
            {t('integrationsPage.webHookForms.save')}
          </ButtonComponent>
        </Grid2>
        <WebhookPayloadEditorModal
          isVisible={isOpenPayloadEditorModal}
          handleClose={() => setIsOpenPayloadEditorModal(false)}
        />
      </Grid2>
    </>
  );
};

export default WebhookEditionForm;
